// Zweck: Benutzerbezogener AB-Cache und sichere Offline-Erledigung.
// Kontext: #1476; nur Revisionskonflikte erneut lesen, fremde Benutzer nicht belasten.
// Stand: 2026-10-20
import localforage from "localforage";
import { completeOrderTask, getOrderTasks } from "./api/orderTasks";
import { getCachedProfile } from "./googleAuthSession";
import type { OrderTaskQueueData, OrderTasks } from "../interfaces/orderTasks";

const store = localforage.createInstance({ name: "360volt-order-tasks" });
const key = (email: string, projectId: string) =>
  `${email.toLowerCase()}:${projectId}`;
let cacheGeneration = 0;

export const cachedOrderTasks = (email: string, projectId: string) =>
  store.getItem<OrderTasks>(key(email, projectId));
export const loadOrderTaskNotes = (email: string, projectId: string) =>
  store.getItem<Record<string, string>>(`notes:${key(email, projectId)}`);
// Pro Projekt serialisieren und nur den betroffenen Entwurf ändern. Ein alter UI-Stand
// darf weder fremde neue Notizen überschreiben noch bestätigte Notizen wiederherstellen.
const noteWrites = new Map<string, Promise<unknown>>();
function changeNotes(
  email: string,
  projectId: string,
  change: (notes: Record<string, string>) => void,
): Promise<void> {
  const storageKey = `notes:${key(email, projectId)}`;
  const next = (noteWrites.get(storageKey) ?? Promise.resolve())
    .catch(() => undefined)
    .then(async () => {
      const notes = { ...((await loadOrderTaskNotes(email, projectId)) ?? {}) };
      change(notes);
      await store.setItem(storageKey, notes);
    });
  noteWrites.set(storageKey, next);
  void next
    .finally(() => {
      if (noteWrites.get(storageKey) === next) noteWrites.delete(storageKey);
    })
    .catch(() => undefined);
  return next;
}
export const saveOrderTaskNote = (
  email: string,
  projectId: string,
  fingerprint: string,
  note: string | null,
) =>
  changeNotes(email, projectId, (notes) => {
    if (note === null) delete notes[fingerprint];
    else notes[fingerprint] = note;
  });

export async function clearOrderTaskCache(email: string): Promise<void> {
  cacheGeneration++;
  const keys = await store.keys();
  await Promise.all(
    keys
      .filter((value) => value.startsWith(`${email.toLowerCase()}:`))
      .map((value) => store.removeItem(value)),
  );
  // Notizen (notes:<email>:<projekt>) bleiben bewusst benutzergebunden erhalten,
  // auch verwaiste Entwürfe: Nutzer können sie explizit verwerfen. Bestätigte,
  // unveränderte Notizen entfernt confirmed(); Logout darf ungesendete Texte nicht löschen.
  // Sie werden niemals einem anderen angemeldeten Benutzer angezeigt oder von ihm versendet.
}

export async function loadOrderTasks(
  email: string,
  projectId: string,
): Promise<OrderTasks> {
  const generation = cacheGeneration;
  const data = await getOrderTasks(projectId);
  if (generation === cacheGeneration)
    await store.setItem(key(email, projectId), data);
  return data;
}

async function confirmed(
  data: OrderTaskQueueData,
  result: OrderTasks,
  generation: number,
): Promise<OrderTasks> {
  if (generation === cacheGeneration)
    await store.setItem(key(data.actorEmail, data.projectId), result);
  await changeNotes(data.actorEmail, data.projectId, (notes) => {
    // Nur den exakt übertragenen Text entfernen, niemals einen späteren Entwurf.
    if (notes[data.fingerprint]?.trim() === data.documentation.trim())
      delete notes[data.fingerprint];
  });
  return result;
}

function requireActor(data: OrderTaskQueueData): void {
  if (
    getCachedProfile()?.email.toLowerCase() !== data.actorEmail.toLowerCase()
  ) {
    throw Object.assign(
      new Error(
        "AB-Aufgabe wartet auf die Anmeldung des ursprünglichen Benutzers.",
      ),
      { orderTaskAwaitingActor: true },
    );
  }
}

export async function processOrderTask(
  data: OrderTaskQueueData,
): Promise<OrderTasks> {
  const generation = cacheGeneration;
  for (let attempt = 0; attempt < 3; attempt++) {
    requireActor(data);
    const current = await getOrderTasks(data.projectId);
    // Während des Abrufs kann die Sitzung wechseln. Fremde Nachweise niemals
    // mit dem neuen Token senden, auch nicht nach einem Revisions-Retry.
    requireActor(data);
    const task = current.tasks.find((t) => t.active && t.id === data.taskId);
    if (
      !current.sourceAvailable ||
      !task ||
      task.fingerprint !== data.fingerprint
    ) {
      throw Object.assign(
        new Error(
          "Die AB wurde geändert. Dokumentation prüfen und erneut zuordnen.",
        ),
        { status: 409 },
      );
    }
    if (task.operationId === data.operationId)
      return confirmed(data, current, generation);
    try {
      const result = await completeOrderTask(data.projectId, data.taskId, {
        expectedRevision: current.revision,
        operationId: data.operationId,
        documentation: data.documentation,
      });
      return confirmed(data, result, generation);
    } catch (error: unknown) {
      // Die API liefert für Revisionen 409 mit diesem Code. Andere 4xx sind fachliche
      // Fehler; blindes Wiederholen würde weder Validierung noch geänderte ABs reparieren.
      const conflict = error as { status?: number; code?: string };
      if (
        attempt === 2 ||
        conflict.status !== 409 ||
        conflict.code !== "ORDER_TASK_REVISION_CONFLICT"
      )
        throw error;
    }
  }
  throw new Error("AB-Aufgabe konnte nicht synchronisiert werden.");
}
